import {
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
  type RefObject,
} from "react";
import {
  FP_ORG_LOGO_ASSET_ID,
  FP_SHOW_WHEN_ATTRIBUTE,
  normalizeSelfClosingFpTags,
  type PublicQuote,
} from "@forgepoint/shared";
import {
  PdfPageViewer,
  type QuoteSection,
} from "@/modules/publicQuote/components/PdfPageViewer";
import { unitMarker } from "@/modules/publicQuote/components/pageBreaks";
import { isNestedSelectableBlock } from "./documentEditor";
import { domElementToReact } from "./domToReact";
import { FpTableUnitWindowContext, tableUnitCount } from "./FpTable";
import {
  dedentAuthoredBody,
  FpTextUnitWindowContext,
  parseTextUnits,
} from "./FpText";
import { PageBreakMarker } from "./PageBreakMarker";
import { QuoteDocumentContext } from "./quoteDocumentContext";
import { shouldShowWhen } from "./showWhen";
import { cascadeBlockStyles } from "./styleCascade";
import type { DocumentAssetUrls } from "./types";

/** Attribute stamped on the render root once pagination has quiesced. */
const RENDER_COMPLETE_ATTRIBUTE = "data-fp-render-complete";

/** Attribute marking a block the renderer must not fragment through. */
const FIT_PAGE_ATTRIBUTE = "data-fp-fit-page";

/** Quiet window the render root must stay layout-stable before settling. */
const SETTLE_QUIET_MS = 150;

interface QuoteDocumentRendererProps {
  documentHtml: string;
  quote: PublicQuote;
  assetUrls?: DocumentAssetUrls;
  /**
   * The organization logo `org-logo` resolves to, when it is not the bound
   * quote's own. The template editor binds stand-in data whose organization
   * carries no logo, so it passes the real one to keep the canvas and preview
   * true to life.
   */
  orgLogoUrl?: string | null;
  mode?: "view";
  /**
   * When true, draws a selectable "Page break" marker in the top margin of
   * each page a page break starts, so breaks can be clicked and deleted in the
   * editor. Off in the customer-facing render, where a page break is an
   * invisible boundary. The marker is page chrome, never document content, so
   * the editor and the preview paginate identically.
   */
  editable?: boolean;
  /** Called once pagination has settled. */
  onSettled?: () => void;
}

/** A run of top-level blocks that always starts on a fresh page. */
interface DocumentRegion {
  skipHeader: boolean;
  skipFirstHeader: boolean;
  skipFooter: boolean;
  /** Node id of the `<fp-page-break>` that opened the region, if any. */
  breakNodeId: string | null;
  blocks: Element[];
}

/** The parsed document, ready to flow through PdfPageViewer. */
interface ParsedDocument {
  sections: QuoteSection[];
  repeatableHeader: ReactNode | null;
  repeatableFooter: ReactNode | null;
  pageFrame: ReactNode | null;
  footerPageOffset: number;
}

/**
 * Renders a stored quote document (HTML plus fp-* tags) through the production
 * pagination stack.
 * @param documentHtml - The stored quote document HTML
 * @param quote - The public quote bound into fp-* components
 * @param assetUrls - Resolved asset URLs keyed by asset id
 * @param orgLogoUrl - The org logo url, overriding the bound quote's own
 * @param editable - Keeps hidden blocks and renders page-break markers
 * @param onSettled - Callback invoked once pagination has settled
 */
export function QuoteDocumentRenderer({
  documentHtml,
  quote,
  assetUrls,
  orgLogoUrl,
  editable = false,
  onSettled,
}: QuoteDocumentRendererProps) {
  const parsed = useMemo(
    () => parseDocument(documentHtml, quote, editable),
    [documentHtml, quote, editable],
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const resolvedLogoUrl = orgLogoUrl ?? quote.organization?.logoUrl ?? null;
  const resolvedAssetUrls = useMemo(
    () => foldOrgLogo(resolvedLogoUrl, assetUrls),
    [resolvedLogoUrl, assetUrls],
  );

  useRenderSettledSignal(
    rootRef,
    parsed.sections,
    quote,
    resolvedAssetUrls,
    resolvedLogoUrl,
    onSettled,
  );

  return (
    <div ref={rootRef} className="quote-document">
      <QuoteDocumentContext.Provider
        value={{
          quote,
          assetUrls: resolvedAssetUrls,
          footerPageOffset: parsed.footerPageOffset,
          editable,
        }}
      >
        <PdfPageViewer
          sections={parsed.sections}
          repeatableHeader={parsed.repeatableHeader ?? undefined}
          repeatableFooter={parsed.repeatableFooter ?? undefined}
          pageOverlay={parsed.pageFrame ?? undefined}
          watermark={quote.watermark}
        />
      </QuoteDocumentContext.Provider>
    </div>
  );
}

/**
 * Folds the org logo into the asset map under `org-logo`, letting an explicit
 * asset url of the same id win.
 * @param logoUrl - The organization's logo url, or null when absent
 * @param assetUrls - The document's resolved asset urls, or undefined
 * @returns The asset map, with the org logo folded in when present
 */
function foldOrgLogo(
  logoUrl: string | null,
  assetUrls: DocumentAssetUrls | undefined,
): DocumentAssetUrls {
  if (!logoUrl) {
    return assetUrls ?? {};
  }
  return { [FP_ORG_LOGO_ASSET_ID]: logoUrl, ...assetUrls };
}

/**
 * Stamps `data-fp-render-complete` and invokes `onSettled` once the render root
 * has stayed layout-stable and fonts are ready. Re-arms whenever the sections
 * or the context-driving props (quote, asset urls) change.
 * @param rootRef - Ref to the render root element
 * @param sections - The parsed sections; a change re-arms the signal
 * @param quote - The bound quote; a change re-arms the signal
 * @param assetUrls - The resolved asset urls; a change re-arms the signal
 * @param orgLogoUrl - The resolved logo url; a change re-arms the signal
 * @param onSettled - Callback invoked once, when the root has settled
 */
function useRenderSettledSignal(
  rootRef: RefObject<HTMLDivElement>,
  sections: QuoteSection[],
  quote: PublicQuote,
  assetUrls: DocumentAssetUrls,
  orgLogoUrl: string | null,
  onSettled?: () => void,
): void {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) {
      return;
    }

    root.removeAttribute(RENDER_COMPLETE_ATTRIBUTE);
    let settled = false;
    let cancelled = false;
    let quietTimer: ReturnType<typeof setTimeout> | null = null;

    const markSettled = () => {
      if (settled || cancelled) {
        return;
      }
      settled = true;
      root.setAttribute(RENDER_COMPLETE_ATTRIBUTE, "true");
      onSettled?.();
    };

    const scheduleSettle = () => {
      if (settled) {
        return;
      }
      if (quietTimer) {
        clearTimeout(quietTimer);
      }
      quietTimer = setTimeout(() => {
        const finish = () => waitForImages(root).then(markSettled);
        const fontsReady = document.fonts?.ready;
        if (fontsReady) {
          fontsReady.then(finish).catch(finish);
          return;
        }
        finish();
      }, SETTLE_QUIET_MS);
    };

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(scheduleSettle);
    resizeObserver?.observe(root);

    const mutationObserver = new MutationObserver(scheduleSettle);
    mutationObserver.observe(root, { childList: true, subtree: true });

    scheduleSettle();

    return () => {
      cancelled = true;
      if (quietTimer) {
        clearTimeout(quietTimer);
      }
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
    };
  }, [rootRef, sections, quote, assetUrls, orgLogoUrl, onSettled]);
}

/**
 * Resolves once every `<img>` under the render root has loaded and decoded.
 * @param root - The render root to scan for images
 * @returns A promise that resolves when all images are loaded/decoded
 */
function waitForImages(root: HTMLElement): Promise<void> {
  const images = Array.from(root.querySelectorAll("img"));
  return Promise.all(images.map(waitForImage)).then(() => undefined);
}

/**
 * Resolves once a single image has loaded and decoded; a failed load or decode
 * resolves rather than rejects so a broken image never blocks settling.
 * @param image - The image element to await
 * @returns A promise that resolves when the image is ready or has failed
 */
function waitForImage(image: HTMLImageElement): Promise<void> {
  const decode = () =>
    image.decode ? image.decode().catch(() => undefined) : Promise.resolve();

  if (image.complete) {
    return decode();
  }

  return new Promise<void>((resolve) => {
    const cleanup = () => {
      image.removeEventListener("load", onLoad);
      image.removeEventListener("error", onError);
    };
    const onLoad = () => {
      cleanup();
      decode().then(() => resolve());
    };
    const onError = () => {
      cleanup();
      resolve();
    };
    image.addEventListener("load", onLoad);
    image.addEventListener("error", onError);
  });
}

/**
 * Parses the stored document: stamps node ids over the raw top-level blocks,
 * drops blocks their condition hides, cascades authored block styles, builds
 * the node index, lifts out the repeatable header and footer, splits the flow
 * into page regions, and converts everything to React through the DOM
 * converter.
 * @param documentHtml - The stored quote document HTML
 * @param quote - The public quote backing the top-level show-when conditions
 * @param editable - Keeps hidden blocks and attaches page-break markers
 * @returns The sections, header, footer, and page-number offset
 */
function parseDocument(
  documentHtml: string,
  quote: PublicQuote,
  editable: boolean,
): ParsedDocument {
  const sourceDoc = new DOMParser().parseFromString(
    normalizeSelfClosingFpTags(documentHtml),
    "text/html",
  );
  stampTopLevelNodeIds(sourceDoc.body);
  dropHiddenTopLevelBlocks(sourceDoc.body, quote, editable);
  cascadeAuthoredBlockStyles(sourceDoc.body);

  const repeatableHeader = extractRepeatable(
    sourceDoc,
    "header",
    "repeatable-header",
  );
  const repeatableFooter = extractRepeatable(
    sourceDoc,
    "footer",
    "repeatable-footer",
  );
  const pageFrame = extractPageFrame(sourceDoc, editable);
  const regions = collectRegions(sourceDoc.body);
  const sections = regions.map((region, index) =>
    regionSection(region, index, quote, editable),
  );

  const leadingSkipFooterRegions = regions.findIndex(
    (region) => !region.skipFooter,
  );
  const footerPageOffset =
    leadingSkipFooterRegions === -1 ? regions.length : leadingSkipFooterRegions;

  return {
    sections,
    repeatableHeader,
    repeatableFooter,
    pageFrame,
    footerPageOffset,
  };
}

/**
 * Lifts the authored `<fp-page-frame>` out of the flow and converts it to a
 * static overlay drawn on every printed page, independent of where content
 * lands. The frame is decorative page chrome: it takes no clicks and never
 * shifts pagination. In the editor it keeps its node id and grows a thin
 * clickable ring along its border, so it can be selected and its props edited
 * while the content underneath stays clickable.
 * @param sourceDoc - The parsed source document (mutated: the block is removed)
 * @param editable - Whether the frame is selectable on the editor canvas
 * @returns The overlay node, or null when the document has no frame
 */
function extractPageFrame(
  sourceDoc: Document,
  editable: boolean,
): ReactNode | null {
  const block = sourceDoc.body.querySelector("fp-page-frame");
  if (!block) {
    return null;
  }
  block.remove();
  const border =
    block.getAttribute("border") ??
    (editable ? EDITOR_EMPTY_FRAME_BORDER : null);
  if (!border) {
    return null;
  }
  const radius = block.getAttribute("radius");
  return (
    <div
      aria-hidden
      data-fp-node={editable ? block.getAttribute("data-fp-node") : undefined}
      style={{
        position: "absolute",
        inset: block.getAttribute("margin") ?? "0",
        border,
        borderRadius: radius ?? undefined,
        pointerEvents: "none",
      }}
    >
      {editable && PAGE_FRAME_HIT_RING}
    </div>
  );
}

/** Stands in for a missing frame border in the editor, so the block stays findable. */
const EDITOR_EMPTY_FRAME_BORDER = "1px dashed #d1d5db";

/** Half the width of the clickable ring the editor draws along a frame's border. */
const PAGE_FRAME_HIT_HALF_WIDTH = 4;

/**
 * Four strips hugging the frame's border, the only part of the overlay that
 * takes clicks in the editor: the interior stays transparent to the content.
 */
const PAGE_FRAME_HIT_RING: ReactNode = (
  ["top", "right", "bottom", "left"] as const
).map((edge) => (
  <div
    key={edge}
    style={{
      position: "absolute",
      pointerEvents: "auto",
      [edge]: -PAGE_FRAME_HIT_HALF_WIDTH,
      ...(edge === "top" || edge === "bottom"
        ? { left: 0, right: 0, height: PAGE_FRAME_HIT_HALF_WIDTH * 2 }
        : { top: 0, bottom: 0, width: PAGE_FRAME_HIT_HALF_WIDTH * 2 }),
    }}
  />
));

/**
 * Converts one page region into a paginator section. A region whose blocks
 * include a line-bound table also carries a fragmenter, letting the paginator
 * ask for a range of the region's units back as real DOM instead of showing the
 * whole region through a clip window; a region with no such table renders
 * exactly as before and keeps clip-window slicing.
 * @param region - The page region to convert
 * @param index - The region's position, which seeds its React keys
 * @param quote - The public quote backing the document
 * @param editable - Whether the editor's page-break marker is drawn
 * @returns The paginator section
 */
function regionSection(
  region: DocumentRegion,
  index: number,
  quote: PublicQuote,
  editable: boolean,
): QuoteSection {
  const key = `region-${index}`;
  const base = {
    key,
    forcePageBreak: true,
    skipHeader: region.skipHeader,
    skipFirstHeader: region.skipFirstHeader,
    skipFooter: region.skipFooter,
    pageMarker:
      editable && region.breakNodeId ? (
        <PageBreakMarker nodeId={region.breakNodeId} />
      ) : undefined,
  };
  const plan = planRegionFragments(region.blocks, quote);
  if (plan === null) {
    return {
      ...base,
      content: (
        <>
          {region.blocks.map((block, blockIndex) =>
            domElementToReact(block, `${key}-block-${blockIndex}`),
          )}
        </>
      ),
    };
  }
  return {
    ...base,
    content: renderRegionUnits(plan, 0, plan.unitCount, key),
    fragmenter: {
      unitCount: plan.unitCount,
      renderUnits: (startUnit: number, endUnit: number) =>
        renderRegionUnits(plan, startUnit, endUnit, key),
    },
  };
}

/**
 * Fragment pagination for a document region.
 *
 * A region taller than a page used to be shown through a clip window: the whole
 * region rendered once per page and each page hid everything outside its band.
 * A table's collapsed borders paint one shared stroke on the row boundary, so
 * clipping there sliced that stroke in half and the closing rule vanished.
 *
 * A region holding a line-bound table is instead split into real per-page DOM:
 * every page renders a complete `<table>` carrying only that page's rows, with
 * its own genuine borders on both edges. The region's blocks, that table's row
 * units, and the text units of long authored `<fp-text>` bodies form one flat
 * unit space; the paginator measures where the units land and asks for a range
 * back. Other blocks stay whole - a block is one unit - and a block whose
 * enclosed table is wrapped (the rail-carrying `<fp-columns>` of a framed
 * letter, say) re-renders its wrapper on every fragment, so rails and insets
 * continue down the pages. A long text body splits the same way: its markdown
 * blocks and top-level list items are its units, so a 25-item clarification
 * list starts under its heading and continues item by item instead of moving
 * to the next page as one lump.
 *
 * Regions with no fragmentable block plan nothing and keep clip-window slicing.
 */

/** One top-level block's slice of its region's unit space. */
interface BlockUnitSpan {
  block: Element;
  /** The block's first unit index. */
  start: number;
  /** One past the block's last unit index. */
  end: number;
  /**
   * What the units are: the block itself as one unit, the row units of the
   * table the block encloses, or the text units of the block's authored body.
   */
  kind: "atomic" | "table" | "text";
}

/** How a region's blocks divide into atomic units. */
interface RegionFragmentPlan {
  unitCount: number;
  spans: BlockUnitSpan[];
}

/**
 * Plans a region's unit space, or null when no block encloses a fragmentable
 * table - in which case the region keeps clip-window slicing untouched.
 * @param blocks - The region's top-level blocks, in document order
 * @param quote - The public quote backing the document
 * @returns The plan, or null when the region must not be fragmented
 */
function planRegionFragments(
  blocks: Element[],
  quote: PublicQuote,
): RegionFragmentPlan | null {
  const spans = blocks.reduce<BlockUnitSpan[]>((planned, block) => {
    const start = planned[planned.length - 1]?.end ?? 0;
    return [...planned, blockSpan(block, quote, start)];
  }, []);
  if (!spans.some((span) => span.kind !== "atomic")) {
    return null;
  }
  return { unitCount: spans[spans.length - 1]?.end ?? 0, spans };
}

/**
 * One block's unit span: its enclosed table's row units when it holds one, else
 * a single unit for the block itself.
 * @param block - The top-level block
 * @param quote - The public quote backing the document
 * @param start - The block's first unit index
 * @returns The block's unit span
 */
function blockSpan(
  block: Element,
  quote: PublicQuote,
  start: number,
): BlockUnitSpan {
  const table = fragmentableTable(block);
  const tableUnits = table === null ? 0 : tableUnitCount(table, quote);
  if (tableUnits > 0) {
    return { block, start, end: start + tableUnits, kind: "table" };
  }
  const textUnits = fragmentableTextUnitCount(block);
  if (textUnits > 1) {
    return { block, start, end: start + textUnits, kind: "text" };
  }
  return { block, start, end: start + 1, kind: "atomic" };
}

/** Attributes that fix a block's box height, which splitting would re-apply
 * per fragment and so rule fragmenting out. */
const FIXED_HEIGHT_ATTRIBUTES = ["height", "min-height", "max-height"];

/**
 * How many text units a block contributes when its authored `<fp-text>` body
 * can be fragmented, or 0 when it cannot: a bound body resolves at render time
 * so its units cannot be planned, and a height-constrained box would re-apply
 * its constraint on every fragment.
 * @param block - The top-level block
 * @returns The block's text unit count, or 0 when it must stay atomic
 */
function fragmentableTextUnitCount(block: Element): number {
  if (
    block.tagName.toLowerCase() !== "fp-text" ||
    block.hasAttribute(FIT_PAGE_ATTRIBUTE) ||
    block.hasAttribute("source") ||
    FIXED_HEIGHT_ATTRIBUTES.some((attribute) => block.hasAttribute(attribute))
  ) {
    return 0;
  }
  return parseTextUnits(dedentAuthoredBody(block.textContent ?? "")).length;
}

/**
 * The line-bound table a block exists to render, or null when the block holds
 * anything else. The table has to be the block's sole content all the way down:
 * a fragment re-renders every element on the path to it, so a sibling alongside
 * it would reprint on every page. A block scaled to fit a page is never split,
 * since its scale is measured against content the fragment no longer holds.
 * @param block - The top-level block
 * @returns The fragmentable `<fp-table>`, or null
 */
function fragmentableTable(block: Element): Element | null {
  if (block.hasAttribute(FIT_PAGE_ATTRIBUTE)) {
    return null;
  }
  if (block.tagName.toLowerCase() === "fp-table") {
    return isFragmentableTable(block) ? block : null;
  }
  const children = Array.from(block.children);
  if (children.length !== 1 || hasOwnText(block)) {
    return null;
  }
  return fragmentableTable(children[0]);
}

/**
 * Whether a table repeats the quote's lines and so splits at its row units. A
 * static table has no repeating units, and a fixed `height` stretches a spacer
 * row to whatever is left, which no longer means anything once the table is
 * split. A `min-height` table does fragment: a table tall enough to overflow a
 * page has already met its minimum, and the windowed render drops the spacer.
 * @param element - The `<fp-table>` element
 * @returns True when the table can be fragmented
 */
function isFragmentableTable(element: Element): boolean {
  if (element.hasAttribute("height")) {
    return false;
  }
  return element.hasAttribute("scope") || element.hasAttribute("filter");
}

/**
 * Whether an element carries text of its own alongside its children.
 * @param element - The element to inspect
 * @returns True when a direct child text node holds non-whitespace text
 */
function hasOwnText(element: Element): boolean {
  return Array.from(element.childNodes).some(
    (node) =>
      node.nodeType === Node.TEXT_NODE && (node.nodeValue ?? "").trim() !== "",
  );
}

/**
 * Renders the units in `[startUnit, endUnit)` as a standalone subtree: the
 * blocks the range touches, with an enclosed table windowed to the rows that
 * fall inside it.
 * @param plan - The region's unit plan
 * @param startUnit - The first unit index to render
 * @param endUnit - One past the last unit index to render
 * @param keyPrefix - The React key prefix for the rendered blocks
 * @returns The rendered fragment
 */
function renderRegionUnits(
  plan: RegionFragmentPlan,
  startUnit: number,
  endUnit: number,
  keyPrefix: string,
): ReactNode {
  return (
    <>
      {plan.spans.flatMap((span, blockIndex) =>
        span.start < endUnit && span.end > startUnit
          ? [
              renderSpan(
                span,
                startUnit,
                endUnit,
                `${keyPrefix}-block-${blockIndex}`,
              ),
            ]
          : [],
      )}
    </>
  );
}

/**
 * One block's contribution to a fragment: an atomic block whole, marked as its
 * own unit, or a fragmentable block re-rendered with its table rows or text
 * units windowed.
 * @param span - The block's unit span
 * @param startUnit - The fragment's first unit index
 * @param endUnit - One past the fragment's last unit index
 * @param key - The React key for the rendered block
 * @returns The rendered block
 */
function renderSpan(
  span: BlockUnitSpan,
  startUnit: number,
  endUnit: number,
  key: string,
): ReactNode {
  const content = domElementToReact(span.block, key);
  if (span.kind === "atomic") {
    return (
      <div key={key} {...unitMarker(span.start)}>
        {content}
      </div>
    );
  }
  if (span.kind === "text") {
    return (
      <FpTextUnitWindowContext.Provider
        key={key}
        value={{
          baseUnitIndex: span.start,
          startUnit: Math.max(startUnit, span.start) - span.start,
          endUnit: Math.min(endUnit, span.end) - span.start,
        }}
      >
        {content}
      </FpTextUnitWindowContext.Provider>
    );
  }
  return (
    <FpTableUnitWindowContext.Provider
      key={key}
      value={{
        baseUnitIndex: span.start,
        startUnit: Math.max(startUnit, span.start) - span.start,
        endUnit: Math.min(endUnit, span.end) - span.start,
      }}
    >
      {content}
    </FpTableUnitWindowContext.Provider>
  );
}

/**
 * Stamps a positional `data-fp-node` id on an element and on every selectable
 * block in its subtree. Nested ids use a dotted child-index path from the block
 * root (e.g. "n2.1.0"), so a nested block is selectable in its own right.
 * @param block - The source element to stamp
 * @param nodeId - The element's positional node id
 */
function stampNodeIds(block: Element, nodeId: string): void {
  stampDescendants(block, nodeId);
  block.setAttribute("data-fp-node", nodeId);
}

/**
 * Stamps the selectable blocks in an element's subtree, without stamping the
 * element itself. `<template>` content is not descended into: template interiors
 * are edited through the enclosing repeater's column editor, never selected on
 * the canvas.
 * @param element - The element whose descendants are scanned
 * @param path - The element's dotted child-index path from the body
 */
function stampDescendants(element: Element, path: string): void {
  Array.from(element.children).forEach((child, index) => {
    if (child.tagName.toLowerCase() === "template") {
      return;
    }
    const childPath = `${path}.${index}`;
    if (isNestedSelectableBlock(child)) {
      stampNodeIds(child, childPath);
      return;
    }
    stampDescendants(child, childPath);
  });
}

/**
 * Stamps a positional node id ("nK", K = index among body element children) on
 * every raw top-level block, before hidden blocks are dropped, so the ids agree
 * with the editor, which resolves ids against the same raw stored DOM. A block
 * the render drops consumes its id like any other: the id simply has no
 * rendered element.
 * @param body - The source document body (mutated: ids stamped in place)
 */
function stampTopLevelNodeIds(body: HTMLElement): void {
  Array.from(body.children).forEach((block, index) => {
    stampNodeIds(block, `n${index}`);
  });
}

/**
 * Drops the top-level blocks whose `data-fp-show-when` condition does not hold,
 * before region splitting: page regions are computed from top-level nodes only,
 * so a hidden page (its content plus its `<fp-page-break>`, each carrying the
 * condition) must drop here for the splitter to skip the blank page. Nested
 * elements are gated at render time instead, where the repeater line scope is
 * available.
 *
 * The editor keeps every block: conditional content stays visible and editable
 * there, marked as hidden by `<FpShowWhen>` rather than vanishing off the
 * canvas.
 * @param body - The source document body (mutated in place)
 * @param quote - The public quote backing the conditions
 * @param editable - Keeps hidden blocks for the editor
 */
function dropHiddenTopLevelBlocks(
  body: HTMLElement,
  quote: PublicQuote,
  editable: boolean,
): void {
  if (editable) {
    return;
  }
  Array.from(body.children).forEach((block) => {
    const condition = block.getAttribute(FP_SHOW_WHEN_ATTRIBUTE);
    if (condition !== null && !shouldShowWhen(quote, null, condition)) {
      block.remove();
    }
  });
}

/**
 * Makes every block's authored text styles reach its whole subtree.
 *
 * Only legacy generic HTML needs this: its descendants carry utility classes
 * that swallow an inherited value, so a style set on a wrapper would otherwise
 * do nothing. An fp-* primitive renders its own box and inherits normally, and
 * container attributes never produce a `style` attribute, so no fp-only
 * document reaches here. This goes when the last generic-HTML document does.
 *
 * Nested blocks are selectable in their own right, so the properties panel
 * writes to them directly and each one has to cascade from where it was
 * authored: starting only from the top level would skip a styled paragraph
 * whose enclosing block styles nothing. The roots are collected before any
 * cascading so that the `inherit` values stamped along the way do not
 * themselves become roots. `querySelectorAll` does not enter `<template>`, so
 * repeater interiors stay out of this as they do everywhere else.
 * @param body - The source document body (mutated in place)
 */
function cascadeAuthoredBlockStyles(body: HTMLElement): void {
  const authoredBlocks = Array.from(body.querySelectorAll("*")).flatMap(
    (element) =>
      element instanceof HTMLElement && element.hasAttribute("style")
        ? [element]
        : [],
  );
  authoredBlocks.forEach((block) => cascadeBlockStyles(block));
}

/**
 * Lifts the authored page chrome out of the flow and converts it to the
 * repeatable page-chrome node, in either authored form: `<fp-header>` and
 * `<fp-footer>`, which are page chrome by definition, or the legacy
 * `<header|footer data-fp-repeat="each-page">`. Its `data-fp-node`
 * stamps ride along so the block selects and text-edits like any other block:
 * it is authored once, so every page's copy carries the same ids, which the
 * selection helpers already handle (see renderedDom.ts). Only
 * `data-fp-repeat` is dropped - it marks the authored source, and the lifted
 * node is no longer a candidate for lifting.
 * @param sourceDoc - The parsed source document (mutated: the block is removed)
 * @param tag - The repeatable tag to lift ("header" or "footer")
 * @param key - The React key for the converted node
 * @returns The converted block, or null when the document has none
 */
function extractRepeatable(
  sourceDoc: Document,
  tag: "header" | "footer",
  key: string,
): ReactNode | null {
  const block = sourceDoc.body.querySelector(
    `${tag}[data-fp-repeat="each-page"], fp-${tag}`,
  );
  if (!block) {
    return null;
  }
  block.remove();
  block.removeAttribute("data-fp-repeat");
  return domElementToReact(block, key);
}

/**
 * Splits the remaining top-level blocks into page regions at `<fp-page-break>`
 * boundaries, carrying the data-fp-skip-* flags and the break's node id onto
 * each region. Editor state deliberately plays no part here: the regions - and
 * so the measured page flow - are identical in the editor and the preview. The
 * first region is kept when no region has blocks, so a document with no blocks
 * still renders one page: without it the editor canvas would vanish under a
 * blank template, or the moment the last block is deleted.
 * @param body - The source document body, after header/footer extraction
 * @returns The non-empty regions in document order, or one blank region when the body has no blocks so an empty document still draws a page
 */
function collectRegions(body: HTMLElement): DocumentRegion[] {
  const regions: DocumentRegion[] = [
    {
      skipHeader: false,
      skipFirstHeader: false,
      skipFooter: false,
      breakNodeId: null,
      blocks: [],
    },
  ];
  Array.from(body.children).forEach((block) => {
    if (block.tagName.toLowerCase() === "fp-page-break") {
      regions.push({
        skipHeader: block.hasAttribute("data-fp-skip-header"),
        skipFirstHeader: block.hasAttribute("data-fp-skip-first-header"),
        skipFooter: block.hasAttribute("data-fp-skip-footer"),
        breakNodeId: block.getAttribute("data-fp-node"),
        blocks: [],
      });
      return;
    }
    const currentRegion = regions[regions.length - 1];
    if (currentRegion.blocks.length === 0) {
      currentRegion.skipHeader =
        currentRegion.skipHeader || block.hasAttribute("data-fp-skip-header");
      currentRegion.skipFirstHeader =
        currentRegion.skipFirstHeader ||
        block.hasAttribute("data-fp-skip-first-header");
      currentRegion.skipFooter =
        currentRegion.skipFooter || block.hasAttribute("data-fp-skip-footer");
    }
    currentRegion.blocks.push(block);
  });
  const nonEmpty = regions.filter((region) => region.blocks.length > 0);
  return nonEmpty.length === 0 ? regions.slice(0, 1) : nonEmpty;
}
